import { useMutation } from "convex/react";
import { useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import {
  ArrowRight,
  ListChecks,
  Pause,
  Play,
  Plus,
  UserPlus,
  Waveform,
} from "@/shared/ui/icons";
import { lazy, Suspense, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { api } from "@/../convex/_generated/api";
import { usePermissions } from "@/core/auth";
import { ChannelNavRail, type ChannelNavItem } from "@/channels/ChannelCreatorPicker";
import { usePanelInstanceState } from "@/shell/usePanelInstanceState";
import type { Id } from "@/../convex/_generated/dataModel";
import { api as cyranoApi } from "@/core/api/client";
import { isMobileSync } from "@/core/hooks";
import { layoutAtom, mobileActiveInstanceIdAtom } from "@/core/state";
import {
  addPanel,
  setActiveTab,
  type Layout,
  type LayoutNode,
  type PanelInstance,
  type TabGroup as TabGroupNode,
} from "@/shell/layout";
import type { PanelViewProps } from "@/shell/PanelRegistry";
import { useDialog } from "@/shell/dialog";

import { Button, Carte, EnTeteDePanneau, Grille, Infobulle, Stat, Vide } from "@/shared/ui";

import {
  dismissedOnboardingSlugAtom,
  requestCreatorWizardAtom,
  requestedOnboardingSlugAtom,
  useCreatorsList,
  wizardPanelMountAtom,
} from "./atoms";
import { AvatarPicker } from "./AvatarPicker";
import { formatFans, formatRevenue, type Creator } from "./mockData";

/**
 * Les deux écrans que Créatrices héberge depuis le 08-10-2026 — Kayla :
 * « Audio, pour être dans Créatrices ou Lab » ; « Validation Vault, avec le
 * Vault ». Audio découpe un long MP3 en vocaux pour le Vault d'une
 * créatrice, Validation est la file des médias analysés avant qu'ils ne
 * deviennent visibles des IA : les deux nourrissent le Vault des créatrices.
 * Chargés à la demande, et derrière `vault.update` — le droit que leurs
 * anciens panneaux exigeaient, qu'embarqués ils ne passent plus par
 * `PanelHost`.
 */
const AudioPanel = lazy(() =>
  import("@/panels/audio").then((m) => ({ default: m.AudioPanel })),
);
const VaultReviewPanel = lazy(() =>
  import("@/panels/vault-review").then((m) => ({ default: m.VaultReviewPanel })),
);
type EcranDesCreatrices = "creatrices" | "audio" | "validation";

/**
 * CreatorsPanel — admin tool : grille de cards par creator avec stats
 * (fans + revenue), couleur d'accent et action toggle bot au hover.
 *
 *   - Lecture: live query Convex `creators.listForAccount`.
 *   - Click card -> ouvre un onglet `creator-detail` dans le layout (split
 *     a droite sur desktop, active-group sur mobile), pattern miroir de
 *     Conversations -> Fan. Plusieurs creator-detail peuvent etre ouverts
 *     en parallele.
 *   - L'edit (modif identite, vault, notifications, slash-commands, flow)
 *     est entierement scope a `CreatorDetailPanel` (un onglet par creator).
 */

const CREATOR_DETAIL_PANEL_ID = "creator-detail";

interface OpenCreatorArgs {
  /** Slug Convex du creator a ouvrir (cle de params pour CreatorDetailPanel). */
  creatorSlug: string;
  /** Instance id du CreatorsPanel d'ou l'action est issue — sert d'ancre
   *  pour le split-edge et de signature pour le linked-group reuse. */
  sourceCreatorsId: string;
  /** Titre dynamique de l'onglet (nom du creator). */
  title: string;
}

interface OpenCreatorResult {
  layout: Layout;
  instanceId: string;
}

/**
 * Ouvre (ou focus si deja existant) un onglet creator-detail dans le layout.
 *
 * Diff avec le pattern Conversations -> Fan : on ajoute le nouvel onglet
 * DANS le meme tab group que Creators (pas en split-edge a droite). L'user
 * veut un onglet a cote de Creators, pas un nouveau panneau cote-a-cote —
 * la grille de cards reste accessible via son propre onglet. Pertinent ici
 * car la grille n'a pas besoin de rester visible quand on edite un creator
 * (vs Conversations ou la liste sert d'index quand on chat).
 *
 *   1. Si un onglet creator-detail existe deja pour ce slug + cette source,
 *      on le focus.
 *   2. Sinon, on ajoute dans le tab group qui contient Creators (devient
 *      l'onglet actif).
 *   3. Fallback active-group (source introuvable).
 */
function openCreatorDetailInLinkedGroup(
  layout: Layout,
  args: OpenCreatorArgs,
): OpenCreatorResult {
  const { creatorSlug, sourceCreatorsId, title } = args;

  // 1) Onglet deja ouvert pour CE creator + CETTE source.
  const existing = findCreatorDetailInstance(
    layout.root,
    creatorSlug,
    sourceCreatorsId,
  );
  if (existing) {
    const group = findGroupContaining(layout.root, existing.id);
    if (!group) return { layout, instanceId: existing.id };
    const idx = group.panels.findIndex((p) => p.id === existing.id);
    if (idx >= 0 && group.activeIndex !== idx) {
      return {
        layout: setActiveTab(layout, group.id, idx),
        instanceId: existing.id,
      };
    }
    return { layout, instanceId: existing.id };
  }

  // 2) Ajout dans le meme tab group que Creators. addPanel place le nouveau
  //    tab a la fin du group et le marque active — c'est le comportement
  //    attendu : on bascule visuellement sur l'onglet creator.
  const sourceGroup = findGroupContaining(layout.root, sourceCreatorsId);
  if (sourceGroup) {
    const res = addPanel(
      layout,
      CREATOR_DETAIL_PANEL_ID,
      { kind: "group", groupId: sourceGroup.id },
      {
        params: { creatorSlug, sourceCreatorsId },
        title,
      },
    );
    return { layout: res.layout, instanceId: res.instanceId };
  }

  // 3) Fallback (source introuvable).
  const res = addPanel(
    layout,
    CREATOR_DETAIL_PANEL_ID,
    { kind: "active-group" },
    {
      params: { creatorSlug, sourceCreatorsId },
      title,
    },
  );
  return { layout: res.layout, instanceId: res.instanceId };
}

function findCreatorDetailInstance(
  node: LayoutNode,
  creatorSlug: string,
  sourceCreatorsId: string,
): PanelInstance | null {
  if (node.kind === "tabs") {
    return (
      node.panels.find(
        (p) =>
          p.panelId === CREATOR_DETAIL_PANEL_ID &&
          p.params?.["creatorSlug"] === creatorSlug &&
          p.params?.["sourceCreatorsId"] === sourceCreatorsId,
      ) ?? null
    );
  }
  for (const child of node.children) {
    const found = findCreatorDetailInstance(child, creatorSlug, sourceCreatorsId);
    if (found) return found;
  }
  return null;
}

function findGroupContaining(
  node: LayoutNode,
  instanceId: string,
): TabGroupNode | null {
  if (node.kind === "tabs") {
    return node.panels.some((p) => p.id === instanceId) ? node : null;
  }
  for (const child of node.children) {
    const found = findGroupContaining(child, instanceId);
    if (found) return found;
  }
  return null;
}

export function CreatorsPanel({ instanceId, params }: PanelViewProps) {
  const { t } = useTranslation();
  const creators = useCreatorsList();
  const [ecran, setEcran] = usePanelInstanceState<EcranDesCreatrices>(
    instanceId,
    params,
    "ecran",
    "creatrices",
  );
  const { permissions } = usePermissions();
  const peutToucherAuVault = permissions?.has("vault.update") ?? false;
  const ecrans: ChannelNavItem[] = [
    { id: "creatrices", icon: UserPlus, label: t("panels.creators.title") },
    ...(peutToucherAuVault
      ? [
          { id: "audio", icon: Waveform, label: t("panels.registry.audio.title") },
          {
            id: "validation",
            icon: ListChecks,
            label: t("panels.registry.vault-review.title"),
          },
        ]
      : []),
  ];
  const updateCreator = useMutation(api.creators.update);
  const queryClient = useQueryClient();
  const setRequestWizard = useSetAtom(requestCreatorWizardAtom);
  const setRequestResumeSlug = useSetAtom(requestedOnboardingSlugAtom);
  const setDismissedSlug = useSetAtom(dismissedOnboardingSlugAtom);
  const setWizardMount = useSetAtom(wizardPanelMountAtom);
  const setLayout = useSetAtom(layoutAtom);
  const setMobileActive = useSetAtom(mobileActiveInstanceIdAtom);
  const dialog = useDialog();

  // Callback ref : expose le conteneur du panneau Creators comme cible
  // portail pour `CreatorOnboardingWizard`.
  const containerRef = useCallback(
    (node: HTMLDivElement | null) => {
      setWizardMount(node);
    },
    [setWizardMount],
  );

  useEffect(() => {
    return () => setWizardMount(null);
  }, [setWizardMount]);

  /** "Ajouter un createur" -> ouvre le wizard. */
  function startAdd() {
    setRequestWizard(true);
  }

  /** "Reprendre l'onboarding" sur une card pending -> re-ouvre le wizard sur
   *  CE créateur. Clear le dismiss + pin via l'atom resume consommé par
   *  CreatorOnboardingWizard. */
  function resumeOnboarding(creator: Creator) {
    setDismissedSlug(null);
    setRequestResumeSlug(creator.slug);
  }

  /** Click sur une card -> ouvre un onglet creator-detail dans le layout
   *  (split a droite / active-group selon device), pattern miroir de
   *  Conversations -> Fan. Si un onglet existe deja pour ce creator + cette
   *  source, il est juste focus. */
  function openCreatorTab(creator: Creator) {
    if (!instanceId) return; // safety : panel pas monte comme panel-host child
    let openedInstanceId: string | null = null;
    setLayout((prev) => {
      const res = openCreatorDetailInLinkedGroup(prev, {
        creatorSlug: creator.slug,
        sourceCreatorsId: instanceId,
        title: creator.name,
      });
      openedInstanceId = res.instanceId;
      return res.layout;
    });
    // Sur mobile (un seul panel visible a la fois), focus directement le
    // nouvel onglet — sans ca l'user resterait sur la grille Creators.
    if (isMobileSync() && openedInstanceId) {
      setMobileActive(openedInstanceId);
    }
  }

  async function handleAvatarChange(id: string, avatar_url: string) {
    await updateCreator({
      creatorId: id as Id<"creators">,
      avatarUrl: avatar_url,
    });
  }

  /** Resout l'UUID Neon a partir du slug Convex puis appelle l'endpoint. */
  async function withCyranoCreatorId<T>(
    slug: string,
    op: (cyranoId: string) => Promise<T>,
  ): Promise<T> {
    const resp = await cyranoApi.get<{ creators: { id: string; slug: string }[] }>(
      "/api/creators",
    );
    const cyranoId = resp.creators?.find((c) => c.slug === slug)?.id;
    if (!cyranoId) {
      throw new Error(`Cyrano creator id not found for slug "${slug}"`);
    }
    return op(cyranoId);
  }

  /** Pause / resume le bot Telegram. Le backend flip `is_enabled` DB ET
   *  aligne la pause Redis globale (`bot_paused_global:{creator_id}`) — UI
   *  doit donc patcher les deux dans le cache pour rester coherente avant
   *  le refetch. Optimistic update + rollback si la requete echoue. */
  type ConnectionsCache = {
    connections: ({
      slug: string;
      bot_is_enabled: boolean | null;
      bot_is_globally_paused?: boolean;
    } & Record<string, unknown>)[];
  };

  async function handleToggleBot(creator: Creator): Promise<void> {
    const previousEnabled = creator.bot_is_enabled;
    const next = creator.status !== "active";
    queryClient.setQueryData<ConnectionsCache | undefined>(
      ["creators", "connections"],
      (old) => {
        if (!old || !Array.isArray(old.connections)) return old;
        return {
          ...old,
          connections: old.connections.map((c) =>
            c.slug === creator.slug
              ? { ...c, bot_is_enabled: next, bot_is_globally_paused: !next }
              : c,
          ),
        };
      },
    );
    try {
      await withCyranoCreatorId(creator.slug, (id) =>
        cyranoApi.post(`/api/creators/${id}/telegram-integration/toggle`, {
          is_enabled: next,
        }),
      );
      await queryClient.invalidateQueries({ queryKey: ["creators", "connections"] });
    } catch (err) {
      queryClient.setQueryData<ConnectionsCache | undefined>(
        ["creators", "connections"],
        (old) => {
          if (!old || !Array.isArray(old.connections)) return old;
          return {
            ...old,
            connections: old.connections.map((c) =>
              c.slug === creator.slug
                ? { ...c, bot_is_enabled: previousEnabled ?? null }
                : c,
            ),
          };
        },
      );
      await queryClient.invalidateQueries({ queryKey: ["creators", "connections"] });
      const message = err instanceof Error ? err.message : String(err);
      const errKey = next
        ? "panels.creators.card.toggleErrorResume"
        : "panels.creators.card.toggleErrorPause";
      void dialog.alert({
        title: t("panels.creators.title"),
        message: `${t(errKey)} : ${message}`,
        variant: "error",
      });
    }
  }

  return (
    <div ref={containerRef} className="cockpit-panel-content @container relative flex overflow-hidden">
      <ChannelNavRail
        items={ecrans}
        activeId={ecran}
        onSelect={(id) => setEcran(id as EcranDesCreatrices)}
        ariaLabel={t("panels.creators.title")}
      />
      {ecran !== "creatrices" ? (
        <div role="tabpanel" className="min-h-0 min-w-0 flex-1 overflow-hidden">
          <Suspense fallback={null}>
            {ecran === "audio" ? (
              <AudioPanel instanceId={instanceId} />
            ) : (
              <VaultReviewPanel instanceId={instanceId} />
            )}
          </Suspense>
        </div>
      ) : (
        <CreatorsGrid
          creators={creators}
          onAdd={startAdd}
          onEdit={openCreatorTab}
          onAvatarChange={(c, url) => handleAvatarChange(c.id, url)}
          onToggleBot={handleToggleBot}
          onResumeOnboarding={resumeOnboarding}
        />
      )}
    </div>
  );
}

/* ============================================================ */
/* CreatorsGrid — l'écran « Créatrices » (en-tête + grille)        */
/* ============================================================ */

export interface CreatorsGridProps {
  creators: Creator[];
  onAdd: () => void;
  onEdit: (creator: Creator) => void;
  onAvatarChange: (creator: Creator, dataUrl: string) => void;
  onToggleBot: (creator: Creator) => void;
  onResumeOnboarding: (creator: Creator) => void;
}

/** Le dessin de l'écran, sans aucune donnée à lui : le panneau lui passe la
 *  liste et les gestes (et le harnais `createurs-dev.html`, une liste inventée). */
export function CreatorsGrid({
  creators,
  onAdd,
  onEdit,
  onAvatarChange,
  onToggleBot,
  onResumeOnboarding,
}: CreatorsGridProps) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      {/* Header */}
      <EnTeteDePanneau
        niveau="h2"
        titre={t("panels.creators.title")}
        sousTitre={
          creators.length === 1
            ? t("panels.creators.creatorOne", { count: creators.length })
            : t("panels.creators.creatorOther", { count: creators.length })
        }
        actions={
          <Button
            variant="primary"
            size="sm"
            onClick={onAdd}
            icon={<Plus className="h-3 w-3" />}
          >
            {t("panels.creators.addCreator")}
          </Button>
        }
      />

      {/* Cards grid — auto-fill responsive */}
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {creators.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <Vide icone={<UserPlus />} titre={t("panels.creators.empty")} />
          </div>
        ) : (
          <Grille min={240}>
            {creators.map((c) => (
              <CreatorCard
                key={c.id}
                creator={c}
                onEdit={() => onEdit(c)}
                onAvatarChange={(url) => onAvatarChange(c, url)}
                onToggleBot={() => onToggleBot(c)}
                onResumeOnboarding={() => onResumeOnboarding(c)}
              />
            ))}
          </Grille>
        )}
      </div>
    </div>
  );
}

/* ============================================================ */
/* CreatorCard                                                     */
/* ============================================================ */

interface CreatorCardProps {
  creator: Creator;
  onEdit: () => void;
  onAvatarChange: (dataUrl: string) => void;
  onToggleBot: () => void;
  onResumeOnboarding: () => void;
}

function CreatorCard({
  creator,
  onEdit,
  onAvatarChange,
  onToggleBot,
  onResumeOnboarding,
}: CreatorCardProps) {
  const { t } = useTranslation();
  const isPaused = creator.status === "paused";
  const isPending = creator.status === "pending";
  const hasIntegration = creator.bot_is_enabled !== null && creator.bot_is_enabled !== undefined;
  const isOnboarding = !hasIntegration;
  const botEnabled = creator.status === "active";
  const libelleBot = t(
    isOnboarding
      ? "panels.creators.card.resumeOnboardingAria"
      : botEnabled
        ? "panels.creators.card.pauseAria"
        : "panels.creators.card.resumeAria",
    { name: creator.name },
  );

  return (
    // Toute la carte ouvre le détail : un vrai bouton posé SOUS le contenu (pas de
    // bouton dans un bouton), le contenu passe par-dessus sans capter le clic —
    // sauf ses propres contrôles (photo, pause, bulle d'aide). `data-cliquable` :
    // la carte se soulève au survol, comme une `Carte onClick`.
    <Carte
      remplissage="aucun"
      data-cliquable=""
      className={"relative overflow-hidden " + (isPaused ? "opacity-70" : "")}
    >
      <button
        type="button"
        onClick={onEdit}
        aria-label={t("panels.creators.card.editAria", { name: creator.name })}
        className="absolute inset-0 z-0 h-full w-full cursor-pointer"
      />

      <div className="pointer-events-none relative z-[1]">
        {/* Top stripe — couleur du creator */}
        <div
          className="h-1 w-full shrink-0"
          style={{ background: creator.color }}
        />

        {/* Body */}
        <div className="flex flex-col gap-3 p-3">
          {/* Identity row */}
          <div className="flex items-start gap-3 pr-9">
            <div className="pointer-events-auto relative shrink-0">
              <AvatarPicker
                name={creator.name}
                color={creator.color}
                avatarUrl={creator.avatar_url}
                onChange={onAvatarChange}
                sizeClass="h-12 w-12"
                textClass="text-lg"
                iconClass="h-3.5 w-3.5"
              />
              <Infobulle
                texte={t(
                  isPaused
                    ? "panels.creators.status.paused"
                    : isPending
                      ? "panels.creators.status.pending"
                      : "panels.creators.status.active",
                )}
              >
                <span
                  aria-hidden="true"
                  className={
                    "regie-round absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 ring-2 ring-canvas " +
                    (isPaused
                      ? "bg-attention"
                      : isPending
                        ? "bg-faint"
                        : "bg-valide")
                  }
                />
              </Infobulle>
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="truncate text-sm font-semibold text-fg">
                {creator.name}
              </span>
              {creator.telegram_handle && (
                <span className="truncate font-mono text-[11px] text-muted">
                  {creator.telegram_handle}
                </span>
              )}
              {creator.description && (
                <p className="line-clamp-1 text-[12px] text-muted">
                  {creator.description}
                </p>
              )}
            </div>
          </div>

          {/* Stats grid — « — » quand le chiffre manque (pas de zéro inventé) ; un chiffre connu s'affiche, bot branché ou non. */}
          <Grille min={96} ecart={8}>
            <Infobulle texte={t("panels.creators.card.fansHint")}>
              <div className="pointer-events-auto" onClick={onEdit}>
                <Stat.StatTile
                  taille="sm"
                  label={t("panels.creators.card.fans")}
                  value={creator.fans_count != null ? formatFans(creator.fans_count) : "—"}
                />
              </div>
            </Infobulle>
            <Stat.StatTile
              taille="sm"
              label={t("panels.creators.card.revenue")}
              value={creator.revenue_cents != null ? formatRevenue(creator.revenue_cents) : "—"}
            />
          </Grille>
        </div>
      </div>

      {/* En pending: "Reprendre l'onboarding". Sinon: Play/Pause du bot. */}
      <div className="pointer-events-auto absolute right-2 top-4 z-[2]">
        <Infobulle
          texte={t(
            isOnboarding
              ? "panels.creators.card.resumeOnboardingTooltip"
              : botEnabled
                ? "panels.creators.card.pauseTooltip"
                : "panels.creators.card.resumeTooltip",
          )}
        >
          <Button
            variant="ghost"
            iconOnly
            onClick={(e) => {
              e.stopPropagation();
              if (isOnboarding) {
                onResumeOnboarding();
              } else {
                onToggleBot();
              }
            }}
            aria-label={libelleBot}
          >
            {isOnboarding ? (
              <ArrowRight className="h-4 w-4" />
            ) : botEnabled ? (
              <Pause className="h-4 w-4" weight="fill" />
            ) : (
              <Play className="h-4 w-4" weight="fill" />
            )}
          </Button>
        </Infobulle>
      </div>
    </Carte>
  );
}
